<template>
  <form :action="formAction" :method="formMethod" @submit="onSubmit" class="survey">
    <div v-for="field in fields" :key="field.name" :class="'survey__field survey__field--' + field.type">
      <m4-form-field
        v-if="field.type === 'textarea'"
        v-bind="formFieldProps(field)"
        v-on="formFieldListeners(field)"
        :value="formValues[field.name]"
        @update:modelValue="(val) => textAreaInput(val, field)"
        :error="getError(field)"
      ></m4-form-field>
      <m4-form-field
        v-else-if="field.slotText"
        v-bind="formFieldProps(field)"
        v-on="formFieldListeners(field)"
        v-model="formValues[field.name]"
        @update:modelValue="() => formFieldUpdateModelValueListener(field)"
        :error="getError(field)"
      >
        {{ field.slotText }}
      </m4-form-field>
      <m4-form-field
        v-else
        v-bind="formFieldProps(field)"
        v-on="formFieldListeners(field)"
        v-model="formValues[field.name]"
        @update:modelValue="() => formFieldUpdateModelValueListener(field)"
        :error="getError(field)"
      ></m4-form-field>
    </div>
    <div class="survey__submit">
      <m4-button ref="survey_form_submit" :variant="sendBtnVariant" :size="sendBtnSize">{{ sendBtnTitle }}</m4-button>
    </div>
    <m4-loader v-if="loading" :width="7" :text="'sending'" font-size="1"></m4-loader>
  </form>
</template>
<script>
export default {
  name: "M4Survey",
  data() {
    return {
      formValues: {},
      errors: {},
      loading: false
    };
  },
  props: {
    formMethod: {
      type: String,
      default: "POST"
    },
    formAction: String,
    fields: Array,
    sendBtnVariant: {
      type: String,
      default: "primary"
    },
    sendBtnSize: {
      type: String,
      default: "lg"
    },
    sendBtnTitle: String
  },
  emits: ["submit"],
  computed: {
    hasErrors() {
      return Object.values(this.errors).filter((error) => error).length > 0;
    }
  },
  beforeMount() {
    this.fields.forEach((field) => {
      if (field.value) {
        this.formValues[field.name] = field.value;
        if (field.type === "select-custom-text" && field.customFieldValue) {
          this.formValues[field.customFieldName] = field.customFieldValue;
        }
      }
    });
  },
  methods: {
    formFieldProps(field) {
      let attrs = Object.assign({}, field);
      delete attrs["configuration"];
      delete attrs["slotText"];

      if (field.type === "checkbox-group") {
        attrs["name"] = field.name + "[]";
      }

      if (field.type === "select-custom-text") {
        if (this.errors[field.customFieldName]) {
          attrs["customFieldError"] = this.errors[field.customFieldName];
        }
      }

      return attrs;
    },
    formFieldListeners(field) {
      let self = this;
      if (field.type === "select-custom-text") {
        return {
          selectboxChange() {
            self.validation(field);
          },
          customFieldInput(value) {
            self.formValues[field.customFieldName] = value;
            self.validation(field, "customFieldInput");
          }
        };
      } else if (field.type === "checkbox") {
        return {
          change() {
            self.validation(field);
          }
        };
      } else if (field.type === "select") {
        return {
          change() {
            self.validation(field);
          }
        };
      } else {
        return {};
      }
    },
    formFieldUpdateModelValueListener(field) {
      this.validation(field);
    },
    textAreaInput(val, field) {
      this.formValues[field.name] = val;
      this.validation(field);
    },
    getError(field) {
      return this.errors[field.name] ? this.errors[field.name] : null;
    },
    validation(onlyField = null, eventName = null) {
      let errors = {};

      this.fields.forEach((field) => {
        let formValue = this.formValues[field.name];
        if (
          field.configuration &&
          field.configuration.required &&
          (onlyField === null || onlyField.name === field.name || this.getError(field))
        ) {
          if (typeof formValue === "string") {
            if (formValue.trim() === "") {
              errors[field.name] = "This field is required";
            }
          } else if (typeof formValue === "object") {
            const selectedLength = Object.keys(formValue).length;
            if (selectedLength <= 0) {
              errors[field.name] = "This field is required";
            } else if (field.configuration.minSelected > 0 && field.configuration.minSelected > selectedLength) {
              errors[field.name] = "Please select " + field.configuration.minSelected + " item(s) at least";
            } else if (field.configuration.maxSelected > 0 && field.configuration.maxSelected < selectedLength) {
              errors[field.name] = "Please select maximum " + field.configuration.maxSelected + " item(s)";
            }
          } else if (typeof formValue === "boolean") {
            if (!formValue) {
              errors[field.name] = "This field is required";
            }
          } else if (!formValue) {
            errors[field.name] = "This field is required";
          }
        }

        // for select-custom-text field only
        if (
          field.configuration &&
          field.configuration.required &&
          field.type === "select-custom-text" &&
          (onlyField === null ||
            (onlyField.customFieldName === field.customFieldName && eventName === "customFieldInput") ||
            this.errors[field.customFieldName])
        ) {
          let canCheckCustomFormValue = false;
          let formValue = this.formValues[field.name];

          field.options.forEach((option) => {
            if (option.textfield && formValue === option.value) {
              canCheckCustomFormValue = true;
            }
          });

          let customFormValue = this.formValues[field.customFieldName];
          if (canCheckCustomFormValue && (!customFormValue || customFormValue.trim() === "")) {
            errors[field.customFieldName] = "This field is required";
          }
        }
      });

      if (Object.keys(errors).length > 0) {
        this.errors = errors;
        return false;
      }

      if (onlyField) {
        this.errors[onlyField.name] = null;
        // for select-custom-text field only
        if (onlyField.type === "select-custom-text") {
          this.errors[onlyField.customFieldName] = null;
        }
      }

      return true;
    },
    submit() {
      return this.$refs.survey_form_submit.$el.click();
    },
    onSubmit(e) {
      if (!this.validation()) {
        e.preventDefault();
        return false;
      }

      if (!this.formAction) {
        e.preventDefault();
        this.$emit("submit");
      }

      if (this.formAction) {
        this.loading = true;
      }
    }
  }
};
</script>
